import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PARTNER_OPTION_DEFAULT } from 'ChartApp/ts/features/charts-shared/constants';
import { FUND_CHART_DATA_KEYS } from 'ChartApp/ts/features/fundamental-chart/constants';
import {
    FundamentalChartData,
    FundamentalChartDataPostParams,
    FundChartAISummarySecurityData,
    FundChartSeriesMetadataResponse,
    FundChartState,
} from 'ChartApp/ts/interfaces/fund-chart.interface';

import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';

import { convertNestedStringToBoolean } from 'Angular/utils/state-utils';
import { formFlattenedObject, formFlattenedParamString } from 'Angular/utils/urls';

@Injectable({
    providedIn: 'root',
})
export class FundChartService {
    serializedKeys: string[] = [];

    constructor(private ajaxService: AjaxService, private urlsService: UrlsService) {
        this.serializedKeys = FUND_CHART_DATA_KEYS.concat(['chartCreator', 'sortColumn', 'sortDirection']);
    }

    chartHasNoData(chartData: FundamentalChartData): boolean {
        // chartHasNoData is true if the chart has undefined, null or empty data
        if (!chartData || !chartData.chart_data) return true;

        // Check each data series, chartHasNoData is false if any are non-empty
        for (let i = 0; i < chartData.chart_data.length; i += 1) {
            for (let j = 0; j < chartData.chart_data[i].length; j += 1) {
                if (chartData.chart_data[i][j].raw_data.length > 0) {
                    return false;
                }
            }
        }
        return true;
    }

    getFlattenedFormData(fundChartState: FundChartState): FundChartState {
        const state = { ...fundChartState };
        return formFlattenedObject(state, this.serializedKeys);
    }

    getChartDataFromUrl(chartDataUrl: string, chartCreator: boolean = false): Observable<FundamentalChartData> {
        return this.ajaxService.getDataObserveBody<FundamentalChartData>(chartDataUrl, { chartCreator }).pipe(
            map((response: FundamentalChartData) => {
                convertNestedStringToBoolean(response.params);
                return response;
            })
        );
    }

    getChartDataFromState(
        fundChartState: FundamentalChartDataPostParams,
        chartCreator: boolean = false
    ): Observable<FundamentalChartData> {
        const chartDataBaseUrl = this.urlsService.CHARTS.FUND_CHART_DATA.json;
        return this.ajaxService
            .postFormEncodedDataObserveBody<FundamentalChartData>(chartDataBaseUrl, {
                ...fundChartState,
                chartCreator,
            })
            .pipe(
                map((response: FundamentalChartData) => {
                    convertNestedStringToBoolean(response.params);
                    return response;
                })
            );
    }

    getSeriesMetadataFromState(
        fundChartState: FundamentalChartDataPostParams
    ): Observable<FundChartSeriesMetadataResponse> {
        // Used only by the Fund Chart Creator partner-embed page. Returns expanded series
        // stubs (security x calc, securitylist + calcs, correlations) with no time-series
        // values — enough to render the Annotate tab.
        const url = this.urlsService.CHARTS.FUND_CHART_CREATOR_SERIES_METADATA;
        return this.ajaxService.postFormEncodedDataObserveBody<FundChartSeriesMetadataResponse>(url, {
            ...fundChartState,
            chartCreator: true,
        });
    }

    getPDFReportUrl(fundChartState: FundChartState, reportTitle: string): Observable<{ pdf_url: string }> {
        const url = this.urlsService.CHARTS.FUND_CHART_DATA.pdf;
        const state = { ...fundChartState, reportTitle };
        const serializedKeys = FUND_CHART_DATA_KEYS.concat([
            'reportTitle',
            'chartAnnotations',
            'axisExtremes',
            'sortColumn',
            'sortDirection',
        ]);
        const data = formFlattenedObject(state, serializedKeys);
        return this.ajaxService.postFormEncodedDataObserveBody(url, data);
    }

    getChartCSVDataFromState(fundChartState: FundChartState): Observable<HttpResponse<Blob>> {
        const chartDataBaseUrl = this.urlsService.CHARTS.FUND_CHART_DATA.csv;
        const state = { ...fundChartState };
        const data = formFlattenedObject(state, this.serializedKeys);
        return this.ajaxService.postFormEncodedDataWithBlobObserveResponse(chartDataBaseUrl, data);
    }

    getChartDataUrl(fundChartState: FundChartState): string {
        // If we do not have securities, correlations, or a security list,
        // we are not requesting data
        const chartUrlParams = this.getChartDataUrlQuery(fundChartState);
        const chartDataBaseUrl = this.urlsService.CHARTS.FUND_CHART_DATA_JSON;
        return `${this.urlsService.getUrl(chartDataBaseUrl)}?${chartUrlParams}`;
    }

    getChartDataUrlQuery(fundChartState: FundChartState): string {
        const state = { ...fundChartState };
        const serializedKeys = FUND_CHART_DATA_KEYS.concat([
            'chartAnnotations',
            'axisExtremes',
            'sortColumn',
            'sortDirection',
        ]);
        return formFlattenedParamString(state, serializedKeys, true);
    }

    /* eslint-disable no-param-reassign */
    getChartEmbedUrl(fundChartState: FundChartState, embedType: string): string {
        const nodeBaseUrl = `${process.env.NODE_BASE_URL}`;
        const urlExtension = this.urlsService.getUrl(this.urlsService.EMBEDS.FUND_CHART, { mode: embedType });
        let state = fundChartState;
        if (!fundChartState.partner) {
            state = { ...fundChartState, partner: PARTNER_OPTION_DEFAULT };
        }
        const queryParams = this.getChartDataUrlQuery(state);
        return `${nodeBaseUrl}${urlExtension}&${queryParams}`;
    }

    getChartShareUrl(baseShareUrl: string, fundChartState: FundChartState): Observable<{ redirectUrl: string }> {
        const data = this.getFlattenedFormData(fundChartState);
        return this.ajaxService.postFormEncodedDataObserveBody<{ redirectUrl: string }>(baseShareUrl, data);
    }

    getChartSaveAsImageUrl(fundChartState: FundChartState): Observable<HttpResponse<{ image_url: string }>> {
        const baseUrl = this.urlsService.EMBEDS.SAVE_AS_IMAGE_FUND_CHART;
        const state = { ...fundChartState };
        const serializedKeys = FUND_CHART_DATA_KEYS.concat([
            'chartAnnotations',
            'axisExtremes',
            'sortColumn',
            'sortDirection',
        ]);
        const data = formFlattenedObject(state, serializedKeys);
        return this.ajaxService.postFormEncodedDataObserveResponse<{ image_url: string }>(baseUrl, data);
    }

    getChartDisclosure(fundChartState: FundChartState): Observable<HttpResponse<string>> {
        const url = this.urlsService.DISCLOSURES.FUND_CHART_DISCLOSURE;
        const data = this.getFlattenedFormData(fundChartState);
        return this.ajaxService.postFormEncodedDataWithTextObserveResponse(url, data);
    }

    getAISummarySecurityData(securityIds: string[]): Observable<FundChartAISummarySecurityData> {
        const url = this.urlsService.CHARTS.FUND_CHART_AI_SUMMARY_SECURITY_DATA;
        const data = { securityIds: JSON.stringify(securityIds) };
        return this.ajaxService.postFormEncodedDataObserveBody<FundChartAISummarySecurityData>(url, data);
    }
}
